import { get, isUndefined, isNumber } from 'lodash';
import { METRIC_NAMES, RESOURCES_CONFIGURATION_INTERVAL as INTERVAL } from '@config/Consts';
import { getApiTimeParams, makeArrayOfStrings } from '@core/utils/utils';
import QueueHealthModel from '@core/models/SkillQueueHealthModel';
import SkillsManagerMetricsModel from '@core/models/SkillManagerMetricsModel';
import BaseClient from '@core/common/BaseClient';
import { RESOURCES as REPORTING_RESOURCES } from '@core/clients/ReportingClient';
import { RESOURCES as AGENT_MANAGER_RESOURCES, METRICS_RESOURCE_PARAMS } from '@core/clients/AgentManagerClient';
import { transformRequestQueryParams } from '@core/common/transformers/transform-request-query-params';

function prepareSkillsListRequest(settings, httpMethod, params, queryParams) {
  const data = {
    ...settings.filters,
  };
  return {
    method: httpMethod,
    data,
    interval: INTERVAL.GENERAL_RESOURCE,
    body: params,
    config: {
      params: queryParams || {},
    },
  };
}

function transformMessagingQueueHealthRequest(settings) {
  // secondary api
  const params = {
    metrics: 'queue,overdue',
  };

  if (settings.filter.skills && settings.filter.skills.length > 0) {
    params.skillIds = settings.filter.skills.toString();
  }
  if (settings.currentPageSkillIds && settings.currentPageSkillIds.length > 0) {
    params.skillIds = settings.currentPageSkillIds;
  } else if (settings.filter.skills && settings.filter.skills.length > 0) {
    params.skillIds = settings.filter.skills.toString();
  }
  return prepareSkillsListRequest(settings, BaseClient.HTTP_METHODS.POST, params);
}

function transformMetricsRequest(settings) {
  // primary api
  const queryParams = {};
  const params = {
    metricsToRetrieveByTime: [
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.CLOSED,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.AVG_WAIT_TIME_FIRST_RESPONSE,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME
        .AVG_TIME_TO_FIRST_RESPONSE_FIRST_ASSIGNMENT,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.AVG_WAIT_TIME,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.AVG_TIME_TO_RESPONSE,
    ],
    metricsToRetrieveCurrentValue: [
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.ASSIGNED,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.HUMAN_AGENT_LOAD,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.ONLINE_AGENTS,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.AWAY_AGENTS,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.BACK_SOON_AGENTS,
      // moved from reporting
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.OVERDUE_IN_QUEUE,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.UNASSIGNED,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.WAIT_TIME_90,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.WAIT_TIME_50,
    ],
    responseSections: [
      METRICS_RESOURCE_PARAMS.RESPONSE_SECTIONS.GROUP_BY,
    ],
    groupBy: METRICS_RESOURCE_PARAMS.REQUEST_GROUP_BY.SKILL,
  };

  params.filters = {};
  if (settings.filter.groupIds && settings.filter.groupIds.length > 0) {
    params.filters.agentGroupIds = makeArrayOfStrings(settings.filter.groupIds);
  }
  if (settings.timeframe) {
    const apiTimeParams = getApiTimeParams(settings.timeframe);
    params.filters.time = { from: apiTimeParams.lastUpdateFrom, to: apiTimeParams.to };
  }
  if (settings.filter.skills && settings.filter.skills.length > 0) {
    params.filters.skillIds = makeArrayOfStrings(settings.filter.skills);
  }
  return prepareSkillsListRequest(
    settings,
    BaseClient.HTTP_METHODS.POST,
    params,
    transformRequestQueryParams(queryParams, settings),
  );
}

function transformMessagingQueueHealthResponse(response) {
  const data = [];
  const queueHealthSkillRecords = get(response, 'skillsListResponse.data.skillsMetrics');
  if (!isUndefined(queueHealthSkillRecords)) {
    Object.keys(queueHealthSkillRecords).forEach((key) => {
      const queueHealthSkillRecord = queueHealthSkillRecords[key];
      const skillRecord = {
        id: key,
        metrics: new QueueHealthModel({
          id: key,
          [METRIC_NAMES.OVERDUE_TOTAL]: get(queueHealthSkillRecord, [METRIC_NAMES.OVERDUE_TOTAL]),
          [METRIC_NAMES.OVERDUE_ASSIGNED]:
            get(queueHealthSkillRecord, [METRIC_NAMES.OVERDUE_ASSIGNED]),
          [METRIC_NAMES.ACTIONABLE]:
            get(queueHealthSkillRecord, [METRIC_NAMES.ACTIONABLE]),
        }),
      };
      data.push(skillRecord);
    });
  }
  return { data };
}

function transformMetricsResponse(response) {
  const result = [];
  const skillMetricRecords = get(response, 'skillsListResponse.data.groupBy.groups');
  if (!isUndefined(skillMetricRecords)) {
    skillMetricRecords.forEach((record) => {
      const recordMetrics = get(record, 'metrics');
      if (!isUndefined(recordMetrics)) {
        const skillLoad = get(recordMetrics, 'humanAgentLoad');
        const skillMetricRecord = {
          id: record.key,
          metrics: new SkillsManagerMetricsModel({
            [METRIC_NAMES.SKILLS_SKILL_NAME]: record.keyDescription || record.key,
            [METRIC_NAMES.SKILL_ASSIGNED_CONVERSATIONS]: get(recordMetrics, 'assignedConversations'),
            [METRIC_NAMES.SKILL_LOAD]: isNumber(skillLoad) ? Math.round(skillLoad * 100) : null,
            [METRIC_NAMES.SKILL_ONLINE_AGENTS]: get(recordMetrics, 'onlineAgents'),
            [METRIC_NAMES.SKILL_AWAY_AGENTS]: get(recordMetrics, 'awayAgents'),
            [METRIC_NAMES.SKILL_BACK_SOON_AGENTS]: get(recordMetrics, 'backSoonAgents'),
            [METRIC_NAMES.SKILL_CLOSED_CONVERSATIONS]: get(recordMetrics, 'closedConversations'),
            [METRIC_NAMES.AVG_WAIT_TIME]: get(recordMetrics, [METRIC_NAMES.AVG_WAIT_TIME]),
            [METRIC_NAMES.AVG_WAIT_TIME_FIRST_RESPONSE]:
              get(recordMetrics, [METRIC_NAMES.AVG_WAIT_TIME_FIRST_RESPONSE]),
            [METRIC_NAMES.AVG_TIME_TO_RESPONSE]:
              get(recordMetrics, [METRIC_NAMES.AVG_TIME_TO_RESPONSE]),
            [METRIC_NAMES.AVG_TIME_TO_FIRST_RESPONSE_FIRST_ASSIGNMENT]:
              get(recordMetrics, [METRIC_NAMES.AVG_TIME_TO_FIRST_RESPONSE_FIRST_ASSIGNMENT]),
            //
            [METRIC_NAMES.SKILL_WAIT_TIME_AGENT_ASSIGNMENT_50_PERCENTILE]:
              get(recordMetrics,
                [METRIC_NAMES.SKILL_WAIT_TIME_AGENT_ASSIGNMENT_50_PERCENTILE]),
            [METRIC_NAMES.SKILL_WAIT_TIME_AGENT_ASSIGNMENT_90_PERCENTILE]:
              get(recordMetrics,
                [METRIC_NAMES.SKILL_WAIT_TIME_AGENT_ASSIGNMENT_90_PERCENTILE]),
            [METRIC_NAMES.OVERDUE_IN_QUEUE]:
              get(recordMetrics, [METRIC_NAMES.OVERDUE_IN_QUEUE]),
            [METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS]:
              get(recordMetrics, [METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS]),
          }),
        };
        result.push(skillMetricRecord);
      }
    });
  }
  const skillsTotalCount = get(response, 'skillsListResponse.data.metadata.count') || result.length;
  return { data: result, skillsTotalCount };
}

const RESOURCE_TRANSFORM_REQUEST_METHOD_MAP = {
  [REPORTING_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH]: transformMessagingQueueHealthRequest,
  [AGENT_MANAGER_RESOURCES.METRICS]: transformMetricsRequest,
};

const RESOURCE_TRANSFORM_RESPONSE_METHOD_MAP = {
  [REPORTING_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH]: transformMessagingQueueHealthResponse,
  [AGENT_MANAGER_RESOURCES.METRICS]: transformMetricsResponse,
};

function transformRequest(resourceName, settings) {
  return RESOURCE_TRANSFORM_REQUEST_METHOD_MAP[resourceName](settings);
}

function transformResponse(resourceName, response) {
  return RESOURCE_TRANSFORM_RESPONSE_METHOD_MAP[resourceName](response);
}

export default {
  transformRequest,
  transformResponse,
};
